/**
 * Полевые справочники с сервера: состояния поля (`reference.field_states`) и
 * типы угодий (`reference.land_category`).
 *
 * Раньше оба списка лежали копией в коде фронта (constants.ts + notACropKinds.ts):
 * добавили состояние в БД — на экране его нет, переименовали — видно старое,
 * выключили — продолжает предлагаться. Справочник вёл разработчик, а не агроном.
 * Теперь единственный источник правды — API:
 *   GET /api/v1/reference/field-states
 *   GET /api/v1/reference/land-categories
 * (backend: api/v1/reference/field_catalogs.py, только чтение, доступно любому
 * залогиненному; наполнение — миграции-сиды 051/070/071).
 *
 * Кэш 5 минут — как у useGrowingGoals: справочник меняется редко, дёргать его
 * на каждый рендер формы незачем.
 */
import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface FieldStateItem {
  id: string;
  code: string;
  /** ru-подпись из справочника; сервер уже откатывается на код, если перевода нет. */
  name: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface LandCategoryItem extends FieldStateItem {
  /** ADR-046 — только пашня гоняет полный севооборот (пар, прогресс посева). */
  runsCropRotation: boolean;
}

/**
 * Подпись по коду. `null` означает «подписи пока нет» — либо код пуст, либо
 * справочник ещё не загрузился; вызывающий сам решает, что показать
 * («Пар», «—», скелетон). Загруженный справочник без такого кода отдаёт сам
 * код — чтобы неизвестное значение было видно, а не молча исчезало.
 */
export type CodeLabeler = (code?: string | null) => string | null;

export const fieldCatalogKeys = {
  fieldStates: () => ['reference', 'field-states'] as const,
  landCategories: () => ['reference', 'land-categories'] as const,
};

interface RawItem {
  id: string;
  code: string;
  name_ru?: string | null;
  color?: string | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  runs_crop_rotation?: boolean | null;
}

interface Envelope {
  items?: RawItem[] | null;
}

/** Конверт `{items}`; массив в корне поддерживаем defensive (как useGrowingGoals). */
function unwrap(resp: Envelope | RawItem[] | null): RawItem[] {
  if (Array.isArray(resp)) return resp;
  return resp?.items ?? [];
}

function toItem(r: RawItem): FieldStateItem {
  return {
    id: r.id,
    code: r.code,
    name: r.name_ru?.trim() || r.code,
    color: r.color ?? null,
    sortOrder: r.sort_order ?? 0,
    isActive: r.is_active ?? true,
  };
}

const STALE_5_MIN = 5 * 60_000;

/** Состояния поля: подтипы пара, АМП, выводное поле, под мелиорацией. */
export function useFieldStates(): UseQueryResult<FieldStateItem[], Error> {
  return useQuery<FieldStateItem[], Error>({
    queryKey: fieldCatalogKeys.fieldStates(),
    queryFn: async () => {
      const resp = await api.get<Envelope | RawItem[]>(
        '/api/v1/reference/field-states',
      );
      return unwrap(resp).map(toItem);
    },
    staleTime: STALE_5_MIN,
  });
}

/** Типы угодий: пашня, пастбище, сенокос, залежь, мн. насаждения, несельхоз. */
export function useLandCategories(): UseQueryResult<LandCategoryItem[], Error> {
  return useQuery<LandCategoryItem[], Error>({
    queryKey: fieldCatalogKeys.landCategories(),
    queryFn: async () => {
      const resp = await api.get<Envelope | RawItem[]>(
        '/api/v1/reference/land-categories',
      );
      return unwrap(resp).map((r) => ({
        ...toItem(r),
        runsCropRotation: r.runs_crop_rotation ?? false,
      }));
    },
    staleTime: STALE_5_MIN,
  });
}

/** Лукап «код → подпись» по загруженному справочнику (см. `CodeLabeler`). */
export function makeCodeLabeler(
  items: ReadonlyArray<{ code: string; name: string }> | undefined,
): CodeLabeler {
  return (code?: string | null) => {
    if (!code) return null;
    if (!items) return null;
    return items.find((i) => i.code === code)?.name ?? code;
  };
}

/** Подпись состояния поля (вид пара, АМП…) из справочника. */
export function useFieldStateLabel(): CodeLabeler {
  const q = useFieldStates();
  return useMemo(() => makeCodeLabeler(q.data), [q.data]);
}

/** Подпись типа угодья из справочника. */
export function useLandCategoryLabel(): CodeLabeler {
  const q = useLandCategories();
  return useMemo(() => makeCodeLabeler(q.data), [q.data]);
}
